import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Users } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Advocate's registry of associates — auto-attached to every case the advocate appears in. */
export const Associates = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/associates").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/associates", form);
      toast.success(`${data.email} added — attached to ${data.attached.length} case(s)` +
        (data.conflict.length ? `; skipped ${data.conflict.length} (opposing side)` : ""));
      setForm({ name: "", email: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/associates/${a.id}`);
      toast.success(`${a.email} removed from your cases`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <section className="border border-border bg-card p-7" data-testid="associates-section">
      <p className="flex items-center gap-2 label-caps"><Users className="h-3.5 w-3.5" /> My associates</p>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        Register the e-mail of each associate in your chamber. They are detected automatically on every reference in
        which you appear — they may upload documents, read the file on screen, follow hearings and read orders, but
        cannot file references, download or pay. An associate whose e-mail matches the opposing counsel is skipped.
      </p>
      <form onSubmit={add} className="mt-6 grid gap-3 sm:grid-cols-[1fr_1.4fr_auto]" data-testid="associate-form">
        <Input required placeholder="Name" value={form.name} data-testid="associate-name-input"
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="rounded-none h-10" />
        <Input required type="email" placeholder="associate@chamber.in" value={form.email} data-testid="associate-email-input"
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="rounded-none h-10" />
        <Button type="submit" disabled={busy} data-testid="associate-add-button"
          className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10 px-5">
          <UserPlus className="h-3.5 w-3.5 mr-2" /> {busy ? "Adding…" : "Add"}
        </Button>
      </form>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-6" data-testid="associates-empty">No associates registered yet.</p>
      ) : (
        <ul className="mt-6 divide-y divide-border border border-border" data-testid="associates-list">
          {rows.map((a) => (
            <li key={a.id} className="px-4 py-3 flex items-center justify-between gap-4" data-testid={`associate-row-${a.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{a.name}</p>
                <p className="text-xs text-muted-foreground font-mono truncate">
                  {a.email} · {a.case_count} case(s) · {a.active ? `last seen ${fmtDate(a.last_login_at)}` : "invitation sent"}
                </p>
              </div>
              <button onClick={() => remove(a)} data-testid={`associate-remove-${a.id}`}
                className="text-red-800 hover:text-red-600 transition-colors duration-200" aria-label="Remove associate">
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
